import React, { useState, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { colors, webColors, fonts } from '../theme';
import Seo from '../components/Seo';
import CircuitBoard from '../components/landing/CircuitBoard';
import { useNavigateWithTransition } from '../components/PageTransition';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';

const ease = [0.22, 1, 0.36, 1];

const Screen = styled.div`
  position: relative;
  height: 100dvh;
  min-height: 560px;
  width: 100%;
  overflow: hidden;
  background: ${colors.bg};
`;

const Brand = styled(motion.div)`
  position: absolute;
  top: 32px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;
  text-align: center;
  pointer-events: none;
`;

const BrandName = styled.h1`
  font-family: ${fonts.display};
  font-size: clamp(1rem, 2vw, 1.3rem);
  font-weight: 700;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: ${colors.white};
  margin-bottom: 8px;
`;

const BrandSub = styled.p`
  font-family: ${fonts.body};
  font-size: 0.78rem;
  letter-spacing: 0.06em;
  color: ${colors.textMuted};
`;

const BrandCue = styled.p`
  margin-top: 12px;
  font-family: ${fonts.mono};
  font-size: 0.6rem;
  font-weight: 500;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: ${colors.textMuted};
  opacity: 0.7;
`;

const Split = styled.div`
  display: flex;
  height: 100%;
  width: 100%;

  @media (max-width: 720px) {
    flex-direction: column;
  }
`;

const panelPalette = {
  coaching: colors,
  web: webColors,
};

const Panel = styled(motion.a)`
  position: relative;
  flex: ${({ $flex }) => $flex};
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0 0 12vh;
  border: none;
  border-top: 2px solid ${({ $mode }) => panelPalette[$mode].accentGlow};
  cursor: pointer;
  overflow: hidden;
  text-decoration: none;
  background: ${({ $mode }) => ($mode === 'coaching' ? '#0a0a0a' : '#0c0a12')};
  filter: ${({ $muted, $active }) => {
    if ($muted) return 'brightness(0.72) saturate(0.8)';
    if ($active) return 'brightness(1.06)';
    return 'brightness(1)';
  }};
  box-shadow: ${({ $active, $mode }) =>
    $active ? `inset 0 0 0 1px ${panelPalette[$mode].accentGlow}, inset 0 60px 100px -60px ${panelPalette[$mode].accentGlow}` : 'none'};
  transition: flex 0.6s cubic-bezier(0.22, 1, 0.36, 1), filter 0.3s ease, box-shadow 0.4s ease;

  &:focus-visible {
    outline: 2px solid ${({ $mode }) => panelPalette[$mode].accent};
    outline-offset: -4px;
  }

  @media (max-width: 720px) {
    flex: 1;
    padding-bottom: 0;
    align-items: center;
  }
`;

const CoachingBg = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(to top, rgba(8,8,8,0.94) 10%, rgba(8,8,8,0.55) 60%, rgba(8,8,8,0.75) 100%),
    url('/pic/cato1.jpg');
  background-size: cover;
  background-position: 50% 22%;
  filter: saturate(0.75) contrast(1.05);
  transform: scale(1.06);
  transition: transform 0.7s ease;

  ${Panel}:hover &,
  ${Panel}:focus-visible & {
    transform: scale(1.11);
  }
`;

const WebBg = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(${webColors.border} 1px, transparent 1px),
    linear-gradient(90deg, ${webColors.border} 1px, transparent 1px);
  background-size: 46px 46px;
  background-color: #0c0a12;
  opacity: 0.55;
`;

const Spotlight = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(
    circle 380px at var(--mx, 50%) var(--my, 50%),
    ${webColors.accentGlow} 0%,
    transparent 70%
  );
  opacity: var(--spot-opacity, 0);
  transition: opacity 0.4s ease;
  pointer-events: none;
`;

const AccentTint = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ $mode }) =>
    $mode === 'coaching'
      ? `linear-gradient(to top, ${colors.accentDim}, transparent 40%)`
      : 'none'};
  mix-blend-mode: ${({ $mode }) => ($mode === 'coaching' ? 'normal' : 'normal')};
  pointer-events: none;
`;

const PanelContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  padding: 0 24px;
`;

const PanelEyebrow = styled.span`
  display: block;
  font-family: ${fonts.body};
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: ${({ $mode }) => (panelPalette[$mode].accent)};
  margin-bottom: 18px;
`;

const PanelTitle = styled.h2`
  font-family: ${({ $mode }) => ($mode === 'coaching' ? fonts.display : fonts.body)};
  font-size: clamp(2.6rem, 7vw, 5.2rem);
  font-weight: 700;
  text-transform: ${({ $mode }) => ($mode === 'coaching' ? 'uppercase' : 'none')};
  letter-spacing: ${({ $mode }) => ($mode === 'coaching' ? '0.01em' : '-0.02em')};
  color: ${colors.white};
  line-height: 1;
  margin-bottom: 20px;
  transform-origin: center;
  transition: transform 0.4s ease;

  ${Panel}:hover &,
  ${Panel}:focus-visible & {
    transform: scale(1.02);
  }
`;

const PanelTag = styled.p`
  font-family: ${fonts.body};
  font-size: 0.95rem;
  color: rgba(240,240,240,0.68);
  max-width: 320px;
  margin: 0 auto 24px;
  line-height: 1.6;

  @media (max-width: 720px) { margin-bottom: 18px; }
`;

const CtaArrow = styled.span`
  display: inline-block;
  transition: transform 0.3s ease;

  ${Panel}:hover &,
  ${Panel}:focus-visible & {
    transform: translateX(5px);
  }
`;

const PanelCta = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: ${fonts.body};
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${colors.white};
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid ${({ $mode }) => panelPalette[$mode].accentGlow};
  padding: 13px 24px;
  border-radius: 6px;
  transition: border-color 0.3s ease, background 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;

  ${Panel}:hover &,
  ${Panel}:focus-visible & {
    border-color: ${({ $mode }) => panelPalette[$mode].accent};
    background: ${({ $mode }) => panelPalette[$mode].accentDim};
    box-shadow: 0 0 26px ${({ $mode }) => panelPalette[$mode].accentGlow};
  }

  @media (max-width: 720px) {
    font-size: 0.66rem;
    padding: 12px 22px;
    letter-spacing: 0.12em;
  }
`;

const Seam = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 1px;
  z-index: 3;
  background: linear-gradient(to bottom, transparent, rgba(255,255,255,0.18), transparent);
  pointer-events: none;

  @media (max-width: 720px) { display: none; }
`;

const DivideMark = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 4;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(8, 8, 8, 0.65);
  backdrop-filter: blur(6px);
  font-family: ${fonts.mono};
  font-size: 0.6rem;
  letter-spacing: 0.06em;
  color: ${colors.textMuted};
  pointer-events: none;

  @media (max-width: 720px) { display: none; }
`;

const CornerNote = styled.p`
  position: absolute;
  bottom: 20px;
  left: 24px;
  z-index: 5;
  font-family: ${fonts.body};
  font-size: 0.68rem;
  letter-spacing: 0.1em;
  color: ${colors.textMuted};
  pointer-events: none;

  @media (max-width: 720px) { display: none; }
`;

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': 'https://catoakay.com/#webpage',
  url: 'https://catoakay.com/',
  name: 'Cato Akay – Coaching & Web Development',
  description:
    'Cato Akay works across two disciplines: engineered performance coaching, and custom web development for businesses and personal brands.',
  inLanguage: 'en',
};

function Landing() {
  const reducedMotion = usePrefersReducedMotion();
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);
  const wrapRef = useRef(null);
  const goTo = useNavigateWithTransition();

  const handleMove = useCallback((e, mode) => {
    if (reducedMotion) return;
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    el.style.setProperty('--mx', `${x}%`);
    el.style.setProperty('--my', `${y}%`);
    if (mode === 'web') el.style.setProperty('--spot-opacity', '1');
  }, [reducedMotion]);

  const handleLeave = useCallback((e) => {
    e.currentTarget.style.setProperty('--spot-opacity', '0');
  }, []);

  const enter = (mode, evt) => {
    // Let modifier-clicks / middle-click behave like a normal link
    // (open in new tab) instead of hijacking navigation.
    if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.button === 1) return;
    evt.preventDefault();
    if (selected) return;
    setSelected(mode);
    goTo(mode, evt);
  };

  return (
    <Screen>
      <Seo
        title="Cato Akay – Coaching & Web Development"
        description="Cato Akay works across two disciplines: engineered performance coaching for athletes, and custom website development for businesses and personal brands. Choose where to go."
        path="/"
        image="/pic/cato1.jpg"
        jsonLd={jsonLd}
      />

      <Brand
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease }}
      >
        <BrandName>Cato Akay</BrandName>
        <BrandSub>One person. Two disciplines.</BrandSub>
        <BrandCue>Choose Your Path</BrandCue>
      </Brand>

      <Seam />
      <DivideMark aria-hidden="true">OR</DivideMark>

      <Split ref={wrapRef}>
        <Panel
          href="/coaching"
          $mode="coaching"
          $flex={hovered === 'coaching' ? 1.18 : hovered === 'web' ? 0.82 : 1}
          $muted={selected && selected !== 'coaching'}
          $active={hovered === 'coaching'}
          onMouseEnter={() => setHovered('coaching')}
          onMouseLeave={() => setHovered(null)}
          onFocus={() => setHovered('coaching')}
          onBlur={() => setHovered(null)}
          onClick={(e) => enter('coaching', e)}
          aria-label="Explore Coaching — opens the coaching experience"
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.1 }}
        >
          <CoachingBg />
          <AccentTint $mode="coaching" />
          <PanelContent>
            <PanelEyebrow $mode="coaching">Performance</PanelEyebrow>
            <PanelTitle $mode="coaching">Coaching</PanelTitle>
            <PanelTag>Engineered performance coaching for driven athletes.</PanelTag>
            <PanelCta $mode="coaching">
              Explore Coaching <CtaArrow>→</CtaArrow>
            </PanelCta>
          </PanelContent>
        </Panel>

        <Panel
          href="/web"
          $mode="web"
          $flex={hovered === 'web' ? 1.18 : hovered === 'coaching' ? 0.82 : 1}
          $muted={selected && selected !== 'web'}
          $active={hovered === 'web'}
          onMouseEnter={() => setHovered('web')}
          onMouseLeave={(e) => { setHovered(null); handleLeave(e); }}
          onMouseMove={(e) => handleMove(e, 'web')}
          onFocus={() => setHovered('web')}
          onBlur={(e) => { setHovered(null); handleLeave(e); }}
          onClick={(e) => enter('web', e)}
          aria-label="Explore Web / IT — opens the web development experience"
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.1 }}
        >
          <WebBg />
          <CircuitBoard revealed={hovered === 'web'} hot={selected === 'web' || hovered === 'web'} />
          <Spotlight />
          <PanelContent>
            <PanelEyebrow $mode="web">Technology</PanelEyebrow>
            <PanelTitle $mode="web">Web / IT</PanelTitle>
            <PanelTag>Custom websites, built by a senior engineer.</PanelTag>
            <PanelCta $mode="web">
              Explore Web / IT <CtaArrow>→</CtaArrow>
            </PanelCta>
          </PanelContent>
        </Panel>
      </Split>

      <CornerNote>Oslo, Norway</CornerNote>
    </Screen>
  );
}

export default Landing;

